import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getFundPools, getProjects, getFiscalYears, createAllocation } from '../services/api';
import { useToken } from '../hooks/useToken';
import type { FundPool, Project, FiscalYear } from '../types';

export default function NewAllocation() {
  const getToken = useToken();
  const navigate = useNavigate();

  const [fundPools, setFundPools] = useState<FundPool[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);

  const [fundPoolId, setFundPoolId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [amount, setAmount] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getToken()
      .then(async (t) => {
        const [fp, pr, fy] = await Promise.all([
          getFundPools(t),
          getProjects(t),
          getFiscalYears(t, { excludeClosed: true }).catch(() => [] as FiscalYear[]),
        ]);
        setFundPools(fp.filter((f) => f.is_active));
        setProjects(pr.filter((p) => p.is_active));
        setFiscalYears(fy);
        // Pre-select current fiscal year
        const current = fy.find((f) => f.is_current);
        if (current) setFiscalYearId(String(current.fiscal_year_id));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingRef(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsedAmount = parseFloat(amount.replace(/[^0-9.]/g, ''));
    if (!fundPoolId) { setError('Please select a Fund Pool.'); return; }
    if (!projectId) { setError('Please select a Linked Project.'); return; }
    if (!fiscalYearId) { setError('Please select a Fiscal Year.'); return; }
    if (isNaN(parsedAmount) || parsedAmount <= 0) { setError('Please enter a valid allocation amount.'); return; }

    setError(null);
    setSubmitting(true);
    try {
      const token = await getToken();
      await createAllocation(token, {
        fund_pool_id: Number(fundPoolId),
        project_id: Number(projectId),
        fiscal_year_id: Number(fiscalYearId),
        allocated_amount: parsedAmount,
      });
      navigate('/allocations');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout pageTitle="Add a New Allocation">
      <p className="text-sm text-gray-500 mb-5">Add a new fund pool allocation for the current fiscal year</p>
      <div className="max-w-md">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {/* Fund Pool */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fund Pool <span className="text-red-500">*</span>
            </label>
            <select
              value={fundPoolId}
              onChange={(e) => setFundPoolId(e.target.value)}
              required
              disabled={loadingRef}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">— Select Fund Pool —</option>
              {fundPools.map((f) => (
                <option key={f.fund_pool_id} value={f.fund_pool_id}>
                  {f.pool_code}{f.description ? ` — ${f.description}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Linked Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Linked Project <span className="text-red-500">*</span>
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              required
              disabled={loadingRef}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">— Select Project —</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
              ))}
            </select>
          </div>

          {/* Annual Allocation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Annual Allocation ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fiscal Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fiscal Year <span className="text-red-500">*</span>
            </label>
            <select
              value={fiscalYearId}
              onChange={(e) => setFiscalYearId(e.target.value)}
              required
              disabled={loadingRef}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">— Select Fiscal Year —</option>
              {fiscalYears.map((fy) => (
                <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>{fy.label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting || loadingRef}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Allocation'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/allocations')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
